import {Form, FormSubmitHandler, BreadcrumbType} from 'platform/components';

import {useParams} from 'react-router-dom';

import {isNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {
  useGetTireTransitQuery,
  usePatchTireTransitMutation,
  usePostTireTransitMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {testIds, settingsRoutes} from '@dms/routes';
import {SettingsTemplate, useSeriesOptions} from '@dms/settings/shared';
import {handleApiError, useBranches} from '@dms/shared';

import {ApiError, useNavigate} from 'shared/web';

import {tireTransitFormSchema, TireTransitFormType} from '../schemas/tireTransitFormSchema';
import {getDefaultValuesFromTireTransit} from '../utils/getDefaultValuesFromTireTransit';
import {TireTransitFormFields} from './TireTransitFormFields';

const breadcrumbs: BreadcrumbType[] = [
  {
    label: i18n.t('entity.tireTransit.labels.title'),
    href: settingsRoutes.tireTransit,
  },
];

export function TireTransitDetail() {
  const {id} = useParams();
  const navigate = useNavigate();
  const {data, isLoading, isError} = useGetTireTransitQuery(
    {transitSettingId: id as string},
    {skip: isNilOrEmpty(id)}
  );
  const branchesQuery = useBranches();
  const {groupedSeries, isSeriesLoading} = useSeriesOptions(['pneu-hotel/tire-transport-document']);

  const getBranchOptions = () =>
    branchesQuery.branchOptions?.map((branch) => ({
      label: branch.label ?? '',
      value: branch.value,
    })) ?? [];

  const [postTireTransit] = usePostTireTransitMutation();
  const [patchTireTransit] = usePatchTireTransitMutation();

  const handleSubmit: FormSubmitHandler<TireTransitFormType> = async (
    data,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    const request = isNil(id)
      ? postTireTransit({
          body: data,
        })
      : patchTireTransit({body: data, transitSettingId: id});

    await request
      .unwrap()
      .then(() => {
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        navigate(settingsRoutes.tireTransit);
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  return (
    <SettingsTemplate
      header={{title: i18n.t('entity.tireTransit.labels.title'), breadcrumbs}}
      isLoading={isLoading}
      isError={isError}
      data-testid={testIds.settings.tireTransit('template')}
    >
      <Form<TireTransitFormType>
        defaultValues={getDefaultValuesFromTireTransit(data)}
        experimentalZodSchema={tireTransitFormSchema}
        onSubmit={handleSubmit}
        shouldWatchForUnsavedChanges
      >
        {(control, _formApi, navigateWithoutBlocker) => (
          <TireTransitFormFields
            control={control}
            getBranchOptions={getBranchOptions}
            groupedSeries={groupedSeries}
            isSeriesLoading={isSeriesLoading}
            navigateWithoutBlocker={navigateWithoutBlocker}
          />
        )}
      </Form>
    </SettingsTemplate>
  );
}
